/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Resolves buyer company admin detail for ViewAppointments company filter changes.
 * @module logic/appointments/buyerCompanySelection
 */

import { getParticipatingCompanyAdminDetail } from "../businessEvents/index.js";

/**
 * Loads executives and participating company data when an admin selects a buyer company.
 * @param {Object} options
 * @param {string} options.eventId
 * @param {string} options.companyId
 * @param {Array<Object>} options.participatingCompanies
 * @param {import("react-router").NavigateFunction} options.navigate
 * @returns {Promise<{ participatingCompanyData: Object|null, buyerExecutives: Array<Object> }>}
 */
export const resolveBuyerCompanySelection = async ({
  eventId,
  companyId,
  participatingCompanies,
  navigate,
}) => {
  const companyDetails = await getParticipatingCompanyAdminDetail(
    eventId,
    companyId,
    navigate,
  );

  const matchingParticipatingCompany =
    (companyDetails?.idEmpresaCompradora || companyDetails?.requerimientos
      ? companyDetails
      : null) ||
    participatingCompanies.find(
      (participatingCompany) =>
        participatingCompany.idEmpresaCompradora === companyId,
    );

  if (!matchingParticipatingCompany) {
    return {
      participatingCompanyData: null,
      buyerExecutives: [],
    };
  }

  return {
    participatingCompanyData: matchingParticipatingCompany,
    buyerExecutives: companyDetails?.ejecutivosCompradoresCompletos || [],
  };
};
